import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { Map } from 'components/map';
import { useCalculateMapDimensions } from 'components/map/utils';
import mapboxGl from 'mapbox-gl';
import { atom, useAtom, useSetAtom } from 'jotai';
import type { MapRef } from 'react-map-gl';
import { WellsLayer } from './wells-layer';
import { WellsLayerSelection } from './wells-layer-selection';
import { mapConfigAtom, wellsLayerFittedAtom } from './atoms';
import { formatNumber } from 'utils/number';
import styles from './wellbore-map.module.scss';

export const SURFACE_HOLE_SOURCE = 'dca-wells-surface-hole';
const wellboreMapRefAtom = atom<MapRef | null>(null);
export const MAX_FIT_ATTEMPTS = 10;

const coord = (v: number) => formatNumber(v, 5, 5, 'decimal', false);

export function boundsKey(b: mapboxGl.LngLatBounds): string {
  const [[w, s], [e, n]] = b.toArray();
  return `${coord(w)},${coord(s)},${coord(e)},${coord(n)}`;
}

interface WellboreMapProps {
  workspaceId: number;
  filterParams: string;
  highlightedUwis?: Set<string>;
}

export const WellboreMap = ({
  workspaceId,
  filterParams,
  highlightedUwis,
}: WellboreMapProps) => {
  const [expanded, setExpanded] = useState(true);
  const containerRef = useRef(null);
  const [mapRef] = useAtom(wellboreMapRefAtom);
  const setFitted = useSetAtom(wellsLayerFittedAtom);
  const pendingFitRef = useRef(true);
  const lastBoundsKeyRef = useRef('');
  const fitAttemptsRef = useRef(0);

  const fitToWells = useCallback(() => {
    if (!pendingFitRef.current || !mapRef) return;
    const map = mapRef.getMap();
    const features = map.querySourceFeatures(SURFACE_HOLE_SOURCE, {
      sourceLayer: 'hits',
    });
    if (features.length === 0) return;

    const bounds = new mapboxGl.LngLatBounds();
    for (const f of features) {
      if (f.geometry.type === 'Point') {
        bounds.extend(f.geometry.coordinates as [number, number]);
      }
    }
    if (bounds.isEmpty()) return;

    const key = boundsKey(bounds);
    if (key === lastBoundsKeyRef.current) {
      pendingFitRef.current = false;
      fitAttemptsRef.current = 0;
      return;
    }

    fitAttemptsRef.current += 1;
    if (fitAttemptsRef.current > MAX_FIT_ATTEMPTS) {
      pendingFitRef.current = false;
      fitAttemptsRef.current = 0;
      return;
    }

    lastBoundsKeyRef.current = key;
    setFitted(true);
    map.fitBounds(bounds, { padding: 80, maxZoom: 12 });
  }, [mapRef, setFitted]);

  useEffect(() => {
    pendingFitRef.current = true;
    lastBoundsKeyRef.current = '';
    fitAttemptsRef.current = 0;
  }, [filterParams]);

  useEffect(() => {
    if (!mapRef) return;
    const map = mapRef.getMap();

    const onSourceData = (e: mapboxGl.MapSourceDataEvent) => {
      if (e.sourceId === SURFACE_HOLE_SOURCE && e.isSourceLoaded) {
        fitToWells();
      }
    };

    map.on('sourcedata', onSourceData);
    if (
      map.getSource(SURFACE_HOLE_SOURCE) &&
      map.isSourceLoaded(SURFACE_HOLE_SOURCE)
    )
      fitToWells();

    return () => {
      map.off('sourcedata', onSourceData);
    };
  }, [mapRef, fitToWells, filterParams]);

  const { latitude, longitude, zoom } = useCalculateMapDimensions({
    points: undefined,
    containerRef,
  });

  return (
    <Accordion expanded={expanded} onClick={() => setExpanded((prev) => !prev)}>
      <AccordionSummary>
        <h2 className={styles.sectionHeading}>Map</h2>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.mapSection}>
          <div className={styles.mapContainer}>
            <div ref={containerRef} className={styles.mapWrapper}>
              <Map
                className={styles.map}
                longitude={longitude}
                latitude={latitude}
                zoom={zoom}
                mapConfigAtom={mapConfigAtom}
                showControls
                showMapFilters
                scopedLayers={<WellsLayerSelection />}
                refAtom={wellboreMapRefAtom}
              >
                <WellsLayer
                  workspaceId={workspaceId}
                  filterParams={filterParams}
                  highlightedUwis={highlightedUwis}
                />
              </Map>
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
